/**
 * Service pour interroger l'API Open Food Facts
 * Documentation: https://world.openfoodfacts.org/data
 */

export interface OpenFoodFactsProduct {
  code: string;
  product_name?: string;
  brands?: string;
  categories?: string;
  image_url?: string;
  image_small_url?: string;
  quantity?: string;
  serving_size?: string;
  nutriscore_grade?: string;
  nova_group?: number;
  ecoscore_grade?: string;
  ingredients_text?: string;
  allergens?: string;
  labels?: string;
  packaging?: string;
  stores?: string;
  countries?: string;
}

export interface ProductLookupResult {
  found: boolean;
  barcode: string;
  product?: {
    name: string;
    brand: string;
    category: string;
    imageUrl: string | null;
    quantity: string;
    nutriscore: string | null;
    ingredients: string | null;
    allergens: string | null;
  };
  error?: string;
}

class OpenFoodFactsService {
  private baseUrl = 'https://world.openfoodfacts.org/api/v2';
  private cache = new Map<string, ProductLookupResult>();
  private cacheTimeout = 5 * 60 * 1000; // 5 minutes

  /**
   * Rechercher un produit par son code-barres
   */
  async lookupBarcode(barcode: string): Promise<ProductLookupResult> {
    // Vérifier le cache
    const cached = this.cache.get(barcode);
    if (cached) {
      return cached;
    }

    try {
      const response = await fetch(
        `${this.baseUrl}/product/${barcode}.json`,
        {
          headers: {
            'User-Agent': 'StockChef/1.0 (https://stockchef.app)',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 0 || !data.product) {
        const result: ProductLookupResult = {
          found: false,
          barcode,
          error: 'Produit non trouvé dans la base Open Food Facts',
        };
        this.cacheResult(barcode, result);
        return result;
      }

      const product = data.product as OpenFoodFactsProduct;

      const result: ProductLookupResult = {
        found: true,
        barcode,
        product: {
          name: product.product_name || 'Nom inconnu',
          brand: product.brands || '',
          category: this.extractMainCategory(product.categories),
          imageUrl: product.image_url || product.image_small_url || null,
          quantity: product.quantity || '',
          nutriscore: product.nutriscore_grade?.toUpperCase() || null,
          ingredients: product.ingredients_text || null,
          allergens: product.allergens || null,
        },
      };

      this.cacheResult(barcode, result);
      return result;
    } catch (error) {
      console.error('Error looking up barcode:', error);
      return {
        found: false,
        barcode,
        error: error instanceof Error ? error.message : 'Erreur lors de la recherche',
      };
    }
  }

  /**
   * Rechercher des produits par nom
   */
  async searchProducts(query: string, limit = 10): Promise<OpenFoodFactsProduct[]> {
    try {
      const response = await fetch(
        `${this.baseUrl}/search?search_terms=${encodeURIComponent(query)}&page_size=${limit}&json=true`,
        {
          headers: {
            'User-Agent': 'StockChef/1.0',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.products || [];
    } catch (error) {
      console.error('Error searching products:', error);
      return [];
    }
  }

  /**
   * Extraire la catégorie principale
   */
  private extractMainCategory(categories?: string): string {
    if (!categories) return '';

    // Les catégories sont séparées par des virgules, on prend la première
    const categoryList = categories.split(',').map(c => c.trim());

    // Essayer de trouver une catégorie en français
    const frenchCategory = categoryList.find(c =>
      c.startsWith('fr:') || !c.includes(':')
    );

    if (frenchCategory) {
      return frenchCategory.replace('fr:', '').replace('en:', '');
    }

    return categoryList[0]?.replace(/^[a-z]{2}:/, '') || '';
  }

  /**
   * Mettre en cache le résultat
   */
  private cacheResult(barcode: string, result: ProductLookupResult): void {
    this.cache.set(barcode, result);

    // Nettoyer le cache après le timeout
    setTimeout(() => {
      this.cache.delete(barcode);
    }, this.cacheTimeout);
  }

  /**
   * Vider le cache
   */
  clearCache(): void {
    this.cache.clear();
  }
}

export const openFoodFactsService = new OpenFoodFactsService();
